/**
 * dinamic-variant-content
 *
 * Re-fetches the "Dinamic variant content" section via Shopify's
 * Section Rendering API on every variant change.
 *
 * Variant-switch signals (multiple, so it works on any theme pattern):
 *
 *   1. URL change  — popstate plus a one-time patch of history.pushState
 *      and history.replaceState. Catches themes that drive variant
 *      switching through the URL.
 *
 *   2. `change` event on `[name="id"]` (input or select) inside the
 *      `/cart/add` form. Catches stock Shopify variant pickers.
 *
 *   3. Click on `[data-swatch-variant-id]`. Catches custom swatch
 *      components that update the form input/URL asynchronously
 *      (Vue/SPA themes — e.g. the `<product-swatch>` pattern used by
 *      Healthyline). The variant id is read straight off the swatch's
 *      data attribute, so we don't have to wait for the theme's own
 *      reactivity to settle.
 *
 *   4. MutationObserver on every `[selected-variant-id]` wrapper.
 *      This is the canonical Vue-bound attribute on themes that mirror
 *      the selected variant on the product wrapper. Fires after the
 *      theme has fully settled — the most reliable signal.
 *
 *   5. MutationObserver on the form's `input[name="id"][value]`
 *      attribute, as a final safety net.
 *
 * On a confirmed change the script requests:
 *     <productUrl>?variant=<id>&section_id=<id>
 * parses the response, locates the matching `[data-dvc-section]`, and
 * swaps `innerHTML` + mirrors the `hidden` attribute. The outer wrapper,
 * inline `<style>` block and asset link stay intact — only per-variant
 * content (title, subtitle, images, button, CSS var --dvc-columns) moves.
 */

(function () {
  if (window.__dvcBooted) return;
  window.__dvcBooted = true;

  // ---- History API patch (catches SPA URL updates) -------------------------
  var fire = function () {
    window.dispatchEvent(new Event('dvc:locationchange'));
  };
  var origPush = history.pushState;
  var origReplace = history.replaceState;
  history.pushState = function () {
    origPush.apply(this, arguments);
    fire();
  };
  history.replaceState = function () {
    origReplace.apply(this, arguments);
    fire();
  };

  function readVariantFromUrl() {
    try {
      return new URLSearchParams(window.location.search).get('variant') || '';
    } catch (e) {
      return '';
    }
  }

  // -------------------------------------------------------------------------

  function DvcController(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.productUrl = el.dataset.productUrl;
    this.lastVariantId = readVariantFromUrl();
    this.pending = false;
    this.queued = null;
    this.observers = [];

    this.onUrlChange = this.onUrlChange.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);

    window.addEventListener('popstate', this.onUrlChange);
    window.addEventListener('dvc:locationchange', this.onUrlChange);
    document.addEventListener('change', this.onFormChange, true);
    document.addEventListener('click', this.onTriggerClick, true);

    this.attachMutationObservers();
  }

  DvcController.prototype.onUrlChange = function () {
    this.update(readVariantFromUrl());
  };

  DvcController.prototype.onFormChange = function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var form = target.closest('form[action*="/cart/add"]');
    if (!form) return;
    var input = form.querySelector('input[name="id"], select[name="id"]');
    if (!input) return;
    var self = this;
    setTimeout(function () {
      if (input.value) self.update(String(input.value));
    }, 20);
  };

  DvcController.prototype.onTriggerClick = function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var trigger = target.closest('[data-swatch-variant-id]');
    if (!trigger) return;
    var id = trigger.getAttribute('data-swatch-variant-id');
    if (!id) return;
    var self = this;
    // Give the theme one tick to settle its own state first
    setTimeout(function () {
      self.update(String(id));
    }, 20);
  };

  DvcController.prototype.attachMutationObservers = function () {
    var self = this;

    // (a) Wrapper element(s) that mirror the selected variant via
    // `selected-variant-id` attribute. The canonical Vue signal.
    document.querySelectorAll('[selected-variant-id]').forEach(function (wrapper) {
      var obs = new MutationObserver(function () {
        var id = wrapper.getAttribute('selected-variant-id');
        if (id) self.update(id);
      });
      obs.observe(wrapper, {
        attributes: true,
        attributeFilter: ['selected-variant-id'],
      });
      self.observers.push(obs);
    });

    // (b) Hidden `[name="id"]` input(s) inside the add-to-cart form.
    // Catches themes that update the input via setAttribute('value', X).
    document
      .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
      .forEach(function (input) {
        var obs = new MutationObserver(function () {
          if (input.value) self.update(String(input.value));
        });
        obs.observe(input, {
          attributes: true,
          attributeFilter: ['value'],
        });
        self.observers.push(obs);
      });
  };

  DvcController.prototype.update = function (id) {
    id = String(id || '');
    if (!id || id === this.lastVariantId) return;
    this.lastVariantId = id;
    if (this.pending) {
      this.queued = id;
      return;
    }
    this.fetch(id);
  };

  DvcController.prototype.fetch = function (id) {
    var self = this;
    this.pending = true;
    var sep = this.productUrl.indexOf('?') === -1 ? '?' : '&';
    var url =
      this.productUrl +
      sep +
      'variant=' + encodeURIComponent(id) +
      '&section_id=' + encodeURIComponent(this.sectionId);

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh =
          doc.querySelector(
            '[data-dvc-section][data-section-id="' + self.sectionId + '"]'
          ) || doc.querySelector('[data-dvc-section]');

        if (fresh) {
          self.el.innerHTML = fresh.innerHTML;
          if (fresh.hasAttribute('hidden')) {
            self.el.setAttribute('hidden', '');
          } else {
            self.el.removeAttribute('hidden');
          }
        } else {
          // No matching section in the response — variant has no data
          self.el.innerHTML = '';
          self.el.setAttribute('hidden', '');
        }
      })
      .catch(function (err) {
        if (window.console && console.error) {
          console.error('[dinamic-variant-content] update failed', err);
        }
      })
      .then(function () {
        self.pending = false;
        if (self.queued && self.queued !== self.lastVariantId) {
          var q = self.queued;
          self.queued = null;
          self.update(q);
        } else {
          self.queued = null;
        }
      });
  };

  function attach() {
    var nodes = document.querySelectorAll('[data-dvc-section]:not([data-dvc-ready])');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('data-dvc-ready', '');
      new DvcController(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
